import React, { FC, useContext, useState } from "react";
import classNames from "classnames";
import Svg from "@natera/material/lib/svg";
import { defineMessages, useIntl } from "react-intl";
import { APIContext, DialogsContext, EventsContext } from "@app/pippa/provider";
import { MessageReaction } from "@app/pippa/models";
import ThumbUp from "@app/pippa/assets/svg/thumb-up.svg";
import ThumbUpFilled from "@app/pippa/assets/svg/thumb-up-filled.svg";
import ThumbDown from "@app/pippa/assets/svg/thumb-down.svg";
import ThumbDownFilled from "@app/pippa/assets/svg/thumb-down-filled.svg";
import "./messageFeedback.scss";

const messages = defineMessages({
  messageFeedbackThumbsUpLabel: {
    id: "pippa.messageFeedback.thumbsUpLabel",
    defaultMessage: "This response was helpful",
  },
  messageFeedbackThumbsDownLabel: {
    id: "pippa.messageFeedback.thumbsDownLabel",
    defaultMessage: "This response was not helpful",
  },
});

interface Props {
  messageId: string;
  reaction?: MessageReaction;
}

type SelectableReaction =
  | MessageReaction.THUMBS_UP
  | MessageReaction.THUMBS_DOWN;

const MessageFeedback: FC<Props> = ({ messageId, reaction }) => {
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    apiProviderState: { chatId },
    submitMessageFeedback,
  } = useContext(APIContext);
  const { setMessageReaction } = useContext(EventsContext);
  const { messageFeedbackDialog } = useContext(DialogsContext);

  const handleClick = (next: SelectableReaction) => {
    // Clicking the already-selected thumb deselects it: the server records a CLEARED
    // event rather than deleting anything, so the full sequence is preserved.
    const isDeselect = reaction === next;
    const value = isDeselect ? MessageReaction.CLEARED : next;

    setIsSubmitting(true);
    setMessageReaction(messageId, isDeselect ? undefined : next);

    submitMessageFeedback({ chatId, messageId, reaction: value })
      .catch((error) => {
        console.error("Failed to submit message feedback:", error);
        setMessageReaction(messageId, reaction);
      })
      .finally(() => setIsSubmitting(false));

    if (value === MessageReaction.THUMBS_DOWN) {
      messageFeedbackDialog.open({ messageId });
    }
  };

  const thumbClassNames = (value: SelectableReaction) =>
    classNames("message-feedback__button", {
      "message-feedback__button--selected": reaction === value,
    });

  return (
    <div className="message-feedback__container">
      <button
        type="button"
        className={thumbClassNames(MessageReaction.THUMBS_UP)}
        aria-label={intl.formatMessage(messages.messageFeedbackThumbsUpLabel)}
        aria-pressed={reaction === MessageReaction.THUMBS_UP}
        disabled={isSubmitting}
        onClick={() => handleClick(MessageReaction.THUMBS_UP)}
      >
        <Svg
          content={
            reaction === MessageReaction.THUMBS_UP ? ThumbUpFilled : ThumbUp
          }
          aria-hidden={true}
        />
      </button>
      <button
        type="button"
        className={thumbClassNames(MessageReaction.THUMBS_DOWN)}
        aria-label={intl.formatMessage(messages.messageFeedbackThumbsDownLabel)}
        aria-pressed={reaction === MessageReaction.THUMBS_DOWN}
        disabled={isSubmitting}
        onClick={() => handleClick(MessageReaction.THUMBS_DOWN)}
      >
        <Svg
          content={
            reaction === MessageReaction.THUMBS_DOWN
              ? ThumbDownFilled
              : ThumbDown
          }
          aria-hidden={true}
        />
      </button>
    </div>
  );
};

export default MessageFeedback;
